"use client";

import {
  WorkplaceRequestCategory,
  WorkplaceRequestTypeElement
} from "@gettactic/api";
import { SearchInput } from "@gettactic/components/src/controls/SearchInput/SearchInput";
import { IconTile } from "@gettactic/components/src/data-display/IconTile";
import { SelectOffices } from "@gettactic/components/src/form/OfficeSelectorInput/SelectOffices";
import { type IconValue } from "@gettactic/components/src/icons";
import { Button } from "@gettactic/components/src/primitives/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@gettactic/components/src/primitives/tooltip";
import { cn } from "@gettactic/helpers";
import { isInformationRequestType } from "@gettactic/helpers/src/workplace-requests/requestTypeKind";
import { ArrowUpRight } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";

const parseIconValue = (value?: string | null): IconValue | null => {
  if (!value) return null;
  const separator = value.indexOf(":");
  if (separator === -1) return null;
  return { set: value.slice(0, separator), icon: value.slice(separator + 1) };
};

/**
 * A type card's description, clamped to 2 lines. Long descriptions get cut
 * off mid-word with no way to read the rest — shows the full text in a
 * hover tooltip once (and only once) it's actually been clamped, matching
 * the same clamped-text pattern used in DescriptionCell.tsx.
 */
const TypeCardDescription: React.FC<{ description: string }> = ({
  description
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isClamped, setIsClamped] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (ref.current) {
      setIsClamped(ref.current.scrollHeight > ref.current.clientHeight);
    }
  }, [description]);

  const text = (
    <div
      ref={ref}
      className="mt-0.5 line-clamp-2 font-inter text-neutral-600 text-xs"
      onTouchStart={() => {
        if (isClamped) setIsOpen(true);
      }}
    >
      {description}
    </div>
  );

  if (!isClamped) return text;

  return (
    <Tooltip open={isOpen} onOpenChange={setIsOpen}>
      <TooltipTrigger asChild>{text}</TooltipTrigger>
      <TooltipContent className="max-w-xs break-words">
        {description}
      </TooltipContent>
    </Tooltip>
  );
};

interface RequestTypePickerProps {
  types: WorkplaceRequestTypeElement[];
  categoriesById: Map<string, WorkplaceRequestCategory>;
  selectedTypeId: string | null;
  onSelect: (typeId: string) => void;
  /** Office the list is currently scoped to; undefined while searching every office. */
  officeId?: string;
  officeName?: string;
  onOfficeChange: (officeId: string) => void;
  /** True for the reservation flow, where the office is dictated elsewhere. */
  officeSelectorDisabled?: boolean;
  isSearchingAllOffices: boolean;
  onSearchAllOffices: () => void;
  /** True while `types` is being (re)fetched — an empty list doesn't yet mean
   *  the office genuinely has none. */
  isLoadingTypes?: boolean;
  /**
   * Bumped by the caller to force the office selector to remount even when
   * `officeId` itself doesn't change — e.g. dismissing the "switch office?"
   * confirm leaves the filter on the original office, but the selector (an
   * uncontrolled component) has already optimistically shown the rejected
   * pick and has no other way to be told to revert.
   */
  officeSelectResetKey?: number;
}

export const RequestTypePicker: React.FC<RequestTypePickerProps> = ({
  types,
  categoriesById,
  selectedTypeId,
  onSelect,
  officeId,
  officeName,
  onOfficeChange,
  officeSelectorDisabled,
  isSearchingAllOffices,
  onSearchAllOffices,
  isLoadingTypes,
  officeSelectResetKey
}) => {
  const [search, setSearch] = useState("");

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? types.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            (t.description ?? "").toLowerCase().includes(q)
        )
      : types;

    const groups = new Map<string, WorkplaceRequestTypeElement[]>();
    for (const type of filtered) {
      const list = groups.get(type.category_id) ?? [];
      list.push(type);
      groups.set(type.category_id, list);
    }
    return Array.from(groups.entries());
  }, [types, search]);

  return (
    <div className="flex flex-col gap-4.5">
      <div className="mb-2 flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <SearchInput
            inputId="search-request-types"
            query={search}
            setQuery={setSearch}
            placeholder={
              officeName
                ? `Search ${officeName} request types...`
                : "Search request types..."
            }
            className={"shadow-xs font-inter"}
            fullWidth
          />
        </div>
        {!isSearchingAllOffices && (
          <div className="w-40 shrink-0">
            {/* Remounts on office change: SelectOffices' own selection is
                seeded once from defaultValue, so it wouldn't otherwise pick
                up a programmatic switch (e.g. confirming the office-switch
                prompt, or picking a cross-office type). officeSelectResetKey
                additionally remounts it when officeId itself doesn't change —
                dismissing the confirm prompt (see the prop doc above). */}
            <SelectOffices
              key={`${officeId}-${officeSelectResetKey ?? 0}`}
              multiselect={false}
              defaultValue={officeId}
              onChange={(ids) => ids[0] && onOfficeChange(ids[0])}
              disabled={officeSelectorDisabled}
              showSelectedLabel
              placeholder="Select office"
              triggerClassName="h-9 w-full"
            />
          </div>
        )}
      </div>

      {isSearchingAllOffices && grouped.length > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 text-xs">
          Showing request types from every office. Picking one files this
          request under its own office instead of {officeName || "the one"}{" "}
          above.
        </div>
      ) : null}

      {grouped.length === 0 ? (
        <div className="py-6 text-center text-sm">
          <p className="text-neutral-400">
            {isLoadingTypes
              ? "Loading request types…"
              : `No request types found${!isSearchingAllOffices && officeName ? ` in ${officeName}` : ""}.`}
          </p>
          {/* Searching all offices would let a type from another office
              through — pointless (and unsafe) when the office itself can't
              change, e.g. the reservation flow that dictates it elsewhere.
              Also withheld while the scoped query is still in flight — an
              empty list here doesn't yet mean the office genuinely has none. */}
          {!isLoadingTypes &&
          !isSearchingAllOffices &&
          !officeSelectorDisabled ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="mt-1 h-auto px-0"
              onClick={onSearchAllOffices}
            >
              Search all offices
            </Button>
          ) : null}
        </div>
      ) : null}

      {grouped.map(([categoryId, categoryTypes]) => {
        const category = categoriesById.get(categoryId);
        return (
          <div key={categoryId}>
            <div className="mb-2 font-semibold text-neutral-500 text-xs uppercase tracking-wide">
              {category?.name || "Other"}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {categoryTypes.map((type) => {
                const isSelected = type.id === selectedTypeId;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => onSelect(type.id)}
                    className={cn(
                      "flex items-start gap-3 rounded-xl border p-3 text-left transition-colors",
                      isSelected
                        ? "border-neutral-900 ring-1 ring-neutral-900/10"
                        : "border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50"
                    )}
                  >
                    <IconTile
                      color={category?.color}
                      icon={parseIconValue(type.icon)}
                      size="md"
                      bordered
                      className="shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1 font-semibold font-inter text-neutral-900 text-sm">
                        <span className="min-w-0">{type.name}</span>
                        {isInformationRequestType(type) ? (
                          <>
                            {/* Marks where Continue leads. The label is a
                                sibling rather than an aria-label on the icon —
                                an aria-label on an <svg> isn't announced
                                reliably. */}
                            <ArrowUpRight
                              aria-hidden="true"
                              className="h-3.5 w-3.5 shrink-0 text-neutral-500"
                            />
                            <span className="sr-only">
                              Filed outside Tactic
                            </span>
                          </>
                        ) : null}
                      </div>
                      {type.description ? (
                        <TypeCardDescription description={type.description} />
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
